import React, { useState, useEffect, useCallback } from "react";
import { adminApi } from "./api";

const COLORS = {
  bg: "#080C18",
  surface: "rgba(255,255,255,0.025)",
  border: "rgba(255,255,255,0.07)",
  indigo: "#6366F1",
  violet: "#7C3AED",
  text: "#F8F7FF",
  muted: "rgba(148,163,184,0.7)",
  green: "#10B981",
  amber: "#F59E0B",
  red: "#EF4444",
};

const PLANS = ["free", "starter", "pro", "team"];
const PURCHASE_STATUSES = ["", "completed", "pending", "refunded"];
const BUG_STATUSES = ["", "open", "in_progress", "resolved", "closed"];
const MODELS = [
  "gemini-2.5-flash-lite",
  "gemini-2.0-flash",
  "gemini-2.0-flash-lite",
  "gemini-1.5-flash",
  "gemini-1.5-pro",
];

function planLabel(plan) {
  if (!plan || plan === "free") return "Free";
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

function formatTokens(value) {
  const n = Number(value || 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function formatUsd(amount) {
  const n = Number(amount || 0);
  if (n === 0) return "$0.00";
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

function stripeStatusColor(status) {
  if (status === "active" || status === "trialing") return COLORS.green;
  if (status === "past_due" || status === "unpaid" || status === "incomplete") return COLORS.red;
  if (status === "canceled" || status === "incomplete_expired") return COLORS.muted;
  return COLORS.muted;
}

function formatStripeStatus(status) {
  if (!status) return "—";
  return status.replace(/_/g, " ");
}

function formatPeriodEnd(ts) {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleDateString();
}

function StripeLinks({ user }) {
  if (!user.stripeCustomerId && !user.stripeSubscriptionId) {
    return <span style={{ color: COLORS.muted }}>—</span>;
  }
  return (
    <span style={{ display: "inline-flex", gap: 8, flexWrap: "wrap" }}>
      {user.stripeCustomerUrl && (
        <a href={user.stripeCustomerUrl} target="_blank" rel="noreferrer" style={{ color: COLORS.indigo, fontSize: 12 }}>
          Customer
        </a>
      )}
      {user.stripeSubscriptionUrl && (
        <a href={user.stripeSubscriptionUrl} target="_blank" rel="noreferrer" style={{ color: COLORS.indigo, fontSize: 12 }}>
          Subscription
        </a>
      )}
    </span>
  );
}

function PanelShell({ title, onClose, children, maxWidth = 1100 }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(8,12,24,0.9)",
        backdropFilter: "blur(12px)",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0D1224",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 20,
          width: "100%",
          maxWidth,
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          animation: "modalPop 0.35s ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 24px",
            borderBottom: `1px solid ${COLORS.border}`,
            flexShrink: 0,
          }}
        >
          <h2 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 22, fontWeight: 600 }}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            style={{ background: "none", border: "none", color: COLORS.muted, fontSize: 24, cursor: "pointer" }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>{children}</div>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
      <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 26, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function inputStyle() {
  return {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${COLORS.border}`,
    background: "rgba(255,255,255,0.04)",
    color: COLORS.text,
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
  };
}

function btnPrimary() {
  return {
    padding: "10px 18px",
    borderRadius: 10,
    border: "none",
    background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet})`,
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  };
}

function btnGhost() {
  return {
    padding: "10px 18px",
    borderRadius: 10,
    border: `1px solid ${COLORS.border}`,
    background: "transparent",
    color: COLORS.muted,
    fontSize: 14,
    cursor: "pointer",
  };
}

function UserEditModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name,
    email: user.email,
    plan: user.plan,
    credits: user.credits,
    password: "",
  });
  const [stripe, setStripe] = useState(null);
  const [loadingStripe, setLoadingStripe] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [planWarning, setPlanWarning] = useState("");

  const loadStripe = useCallback(async () => {
    setLoadingStripe(true);
    try {
      const data = await adminApi.user(user.id);
      setStripe(data.stripe);
      if (data.user) {
        setForm((f) => ({
          ...f,
          name: data.user.name,
          email: data.user.email,
          plan: data.user.plan,
          credits: data.user.credits,
        }));
      }
    } catch (err) {
      setError(err.message || "Failed to load Stripe info");
    } finally {
      setLoadingStripe(false);
    }
  }, [user.id]);

  useEffect(() => {
    loadStripe();
  }, [loadStripe]);

  useEffect(() => {
    if (stripe?.subscriptionId && form.plan !== user.plan) {
      setPlanWarning(
        "This user has a linked Stripe subscription. Changing the plan here does not cancel Stripe billing — cancel the subscription first or use Sync from Stripe."
      );
    } else {
      setPlanWarning("");
    }
  }, [form.plan, stripe?.subscriptionId, user.plan]);

  const save = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const body = {
        name: form.name,
        email: form.email,
        plan: form.plan,
        credits: Number(form.credits),
      };
      if (form.password) body.password = form.password;
      const data = await adminApi.updateUser(user.id, body);
      if (data.warning) setPlanWarning(data.warning);
      if (data.stripe) setStripe(data.stripe);
      setMessage(data.warning ? "Saved (see warning)" : "Saved");
      onSaved(data.user);
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const syncStripe = async () => {
    setSyncing(true);
    setError("");
    setMessage("");
    try {
      const data = await adminApi.syncUserStripe(user.id);
      setStripe(data.stripe);
      setMessage(data.message || "Synced from Stripe");
      onSaved(data.user);
    } catch (err) {
      setError(err.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const cancelStripe = async () => {
    if (
      !window.confirm(
        "Cancel this user's Stripe subscription immediately? They will be moved to the Free plan and billing stops."
      )
    ) {
      return;
    }
    setCanceling(true);
    setError("");
    setMessage("");
    try {
      const data = await adminApi.cancelUserStripe(user.id);
      setStripe(data.stripe);
      setForm((f) => ({ ...f, plan: data.user.plan, credits: data.user.credits }));
      setMessage(data.message || "Subscription canceled");
      onSaved(data.user);
    } catch (err) {
      setError(err.message || "Cancel failed");
    } finally {
      setCanceling(false);
    }
  };

  const subStatus = stripe?.subscription?.status;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#12182B",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 16,
          padding: 28,
          width: "100%",
          maxWidth: 440,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <h3 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 18, marginBottom: 20 }}>Edit user</h3>

        <div
          style={{
            background: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 12,
            padding: 16,
            marginBottom: 20,
          }}
        >
          <div style={{ fontSize: 12, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10 }}>
            Stripe subscription
          </div>
          {loadingStripe ? (
            <p style={{ color: COLORS.muted, fontSize: 13, margin: 0 }}>Loading Stripe info…</p>
          ) : (
            <>
              <div style={{ display: "grid", gap: 8, fontSize: 13, marginBottom: 12 }}>
                <div>
                  Status:{" "}
                  <span style={{ color: stripeStatusColor(subStatus), fontWeight: 600 }}>
                    {stripe?.subscriptionId ? formatStripeStatus(subStatus || "linked") : "No subscription"}
                  </span>
                  {stripe?.subscription?.cancelAtPeriodEnd && (
                    <span style={{ color: COLORS.muted, marginLeft: 8 }}>(cancels at period end)</span>
                  )}
                </div>
                {stripe?.subscriptionId && (
                  <div style={{ color: COLORS.muted }}>
                    Renews / ends: {formatPeriodEnd(stripe.subscription?.currentPeriodEnd)}
                  </div>
                )}
                <div style={{ display: "flex", gap: 12 }}>
                  {stripe?.customerUrl && (
                    <a href={stripe.customerUrl} target="_blank" rel="noreferrer" style={{ color: COLORS.indigo }}>
                      Open in Stripe →
                    </a>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" onClick={syncStripe} disabled={syncing || !stripe?.subscriptionId} style={btnGhost()}>
                  {syncing ? "Syncing…" : "Sync from Stripe"}
                </button>
                <button
                  type="button"
                  onClick={cancelStripe}
                  disabled={canceling || !stripe?.subscriptionId}
                  style={{ ...btnGhost(), color: COLORS.red, borderColor: "rgba(239,68,68,0.3)" }}
                >
                  {canceling ? "Canceling…" : "Cancel subscription"}
                </button>
              </div>
            </>
          )}
        </div>

        <label style={{ display: "block", marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: COLORS.muted, display: "block", marginBottom: 6 }}>Name</span>
          <input style={inputStyle()} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label style={{ display: "block", marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: COLORS.muted, display: "block", marginBottom: 6 }}>Email</span>
          <input style={inputStyle()} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </label>
        <label style={{ display: "block", marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: COLORS.muted, display: "block", marginBottom: 6 }}>Plan</span>
          <select
            style={inputStyle()}
            value={form.plan}
            onChange={(e) => setForm({ ...form, plan: e.target.value })}
          >
            {PLANS.map((p) => (
              <option key={p} value={p}>{planLabel(p)}</option>
            ))}
          </select>
        </label>
        <label style={{ display: "block", marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: COLORS.muted, display: "block", marginBottom: 6 }}>Credits</span>
          <input
            type="number"
            style={inputStyle()}
            value={form.credits}
            onChange={(e) => setForm({ ...form, credits: e.target.value })}
          />
        </label>
        {planWarning && (
          <p style={{ color: "#F59E0B", fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>{planWarning}</p>
        )}
        <label style={{ display: "block", marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: COLORS.muted, display: "block", marginBottom: 6 }}>New password (optional)</span>
          <input
            type="password"
            style={inputStyle()}
            placeholder="Leave blank to keep current"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </label>
        {error && <p style={{ color: COLORS.red, fontSize: 13, marginBottom: 12 }}>{error}</p>}
        {message && <p style={{ color: COLORS.green, fontSize: 13, marginBottom: 12 }}>{message}</p>}
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button type="button" onClick={save} disabled={saving} style={btnPrimary()}>{saving ? "Saving…" : "Save changes"}</button>
          <button type="button" onClick={onClose} style={btnGhost()}>Close</button>
        </div>
      </div>
    </div>
  );
}

function UserDetailModal({ userId, onClose, onEdit }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [validations, setValidations] = useState([]);
  const [aiUsage, setAiUsage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await adminApi.user(userId);
        if (cancelled) return;
        setUser(data.user);
        setStripe(data.stripe);
        setPurchases(data.purchases || []);
        setValidations(data.validations || []);
        setAiUsage(data.aiUsage || null);
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load user");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const subStatus = stripe?.subscription?.status;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#12182B",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 16,
          padding: 28,
          width: "100%",
          maxWidth: 720,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <h3 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 20, marginBottom: 4 }}>
              {user?.name || "User details"}
            </h3>
            {user && <p style={{ color: COLORS.muted, fontSize: 14, margin: 0 }}>{user.email}</p>}
          </div>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", color: COLORS.muted, fontSize: 24, cursor: "pointer" }}>
            ×
          </button>
        </div>

        {loading && <p style={{ color: COLORS.muted }}>Loading…</p>}
        {error && <p style={{ color: COLORS.red, fontSize: 14 }}>{error}</p>}

        {!loading && user && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: 12,
                marginBottom: 24,
              }}
            >
              <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 4 }}>Plan</div>
                <div style={{ fontWeight: 600 }}>{planLabel(user.plan)}</div>
              </div>
              <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 4 }}>Credits</div>
                <div style={{ fontWeight: 600 }}>{user.credits}</div>
              </div>
              <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 4 }}>Validations</div>
                <div style={{ fontWeight: 600 }}>{user.validationCount ?? 0}</div>
              </div>
              <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 4 }}>AI tokens</div>
                <div style={{ fontWeight: 600 }}>{formatTokens(aiUsage?.totals?.totalTokens ?? user.aiTotalTokens ?? 0)}</div>
              </div>
              <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 4 }}>Est. AI cost</div>
                <div style={{ fontWeight: 600 }}>{formatUsd(aiUsage?.totals?.estimatedCostUsd ?? user.aiEstimatedCostUsd ?? 0)}</div>
              </div>
              <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 4 }}>Stripe</div>
                <div style={{ fontWeight: 600, color: stripeStatusColor(subStatus), fontSize: 13 }}>
                  {stripe?.subscriptionId ? formatStripeStatus(subStatus || "linked") : "—"}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <StripeLinks user={user} />
            </div>

            <h4 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 15, marginBottom: 10 }}>AI usage</h4>
            {!aiUsage || aiUsage.totals?.requestCount === 0 ? (
              <p style={{ color: COLORS.muted, fontSize: 13, marginBottom: 24 }}>
                No AI requests logged yet for this user.
              </p>
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                    gap: 10,
                    marginBottom: 16,
                  }}
                >
                  <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12 }}>
                    <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 4 }}>Requests</div>
                    <div style={{ fontWeight: 600 }}>{aiUsage.totals.requestCount}</div>
                  </div>
                  <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12 }}>
                    <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 4 }}>Input tokens</div>
                    <div style={{ fontWeight: 600 }}>{formatTokens(aiUsage.totals.promptTokens)}</div>
                  </div>
                  <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12 }}>
                    <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 4 }}>Output tokens</div>
                    <div style={{ fontWeight: 600 }}>{formatTokens(aiUsage.totals.completionTokens)}</div>
                  </div>
                  <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12 }}>
                    <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 4 }}>Total tokens</div>
                    <div style={{ fontWeight: 600 }}>{formatTokens(aiUsage.totals.totalTokens)}</div>
                  </div>
                  <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12 }}>
                    <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 4 }}>Est. cost</div>
                    <div style={{ fontWeight: 600 }}>{formatUsd(aiUsage.totals.estimatedCostUsd)}</div>
                  </div>
                </div>

                {aiUsage.byOperation?.length > 0 && (
                  <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 16 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                          {["Operation", "Requests", "Tokens", "Est. cost"].map((h) => (
                            <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: COLORS.muted }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {aiUsage.byOperation.map((row) => (
                          <tr key={row.operation} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                            <td style={{ padding: "8px 10px" }}>{row.label}</td>
                            <td style={{ padding: "8px 10px" }}>{row.requestCount}</td>
                            <td style={{ padding: "8px 10px" }}>{formatTokens(row.totalTokens)}</td>
                            <td style={{ padding: "8px 10px" }}>{formatUsd(row.estimatedCostUsd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {aiUsage.recent?.length > 0 && (
                  <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 24 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                          {["When", "Operation", "Model", "In", "Out", "Total", "Cost"].map((h) => (
                            <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: COLORS.muted }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {aiUsage.recent.map((row) => (
                          <tr key={row.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                            <td style={{ padding: "8px 10px", color: COLORS.muted, whiteSpace: "nowrap" }}>
                              {new Date(row.createdAt).toLocaleString()}
                            </td>
                            <td style={{ padding: "8px 10px" }}>{row.label}</td>
                            <td style={{ padding: "8px 10px", color: COLORS.muted }}>{row.model}</td>
                            <td style={{ padding: "8px 10px" }}>{formatTokens(row.promptTokens)}</td>
                            <td style={{ padding: "8px 10px" }}>{formatTokens(row.completionTokens)}</td>
                            <td style={{ padding: "8px 10px" }}>{formatTokens(row.totalTokens)}</td>
                            <td style={{ padding: "8px 10px" }}>{formatUsd(row.estimatedCostUsd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            <h4 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 15, marginBottom: 10 }}>Purchases</h4>
            {purchases.length === 0 ? (
              <p style={{ color: COLORS.muted, fontSize: 13, marginBottom: 24 }}>No purchases yet.</p>
            ) : (
              <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 24 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                      {["Plan", "Amount", "Status", "Stripe sub", "Date"].map((h) => (
                        <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: COLORS.muted }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map((p) => (
                      <tr key={p.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                        <td style={{ padding: "8px 10px" }}>{planLabel(p.plan)}</td>
                        <td style={{ padding: "8px 10px" }}>${(p.amountCents / 100).toFixed(2)}</td>
                        <td style={{ padding: "8px 10px" }}>{p.status}</td>
                        <td style={{ padding: "8px 10px" }}>
                          {p.stripeSubscriptionUrl ? (
                            <a href={p.stripeSubscriptionUrl} target="_blank" rel="noreferrer" style={{ color: COLORS.indigo, fontSize: 11 }}>
                              {p.stripeSubscriptionId?.slice(0, 18)}…
                            </a>
                          ) : (
                            <span style={{ color: COLORS.muted }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: "8px 10px", color: COLORS.muted }}>{new Date(p.createdAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <h4 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 15, marginBottom: 10 }}>Validations</h4>
            {validations.length === 0 ? (
              <p style={{ color: COLORS.muted, fontSize: 13, marginBottom: 24 }}>No validations yet.</p>
            ) : (
              <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 10, marginBottom: 24 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                      {["Idea", "Verdict", "Score", "Date"].map((h) => (
                        <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: COLORS.muted }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {validations.map((v) => (
                      <tr key={v.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                        <td style={{ padding: "8px 10px", color: COLORS.muted, maxWidth: 280 }}>
                          {v.idea?.length > 70 ? `${v.idea.slice(0, 70)}…` : v.idea}
                        </td>
                        <td style={{ padding: "8px 10px" }}>{v.verdict || "—"}</td>
                        <td style={{ padding: "8px 10px" }}>{v.overallScore ?? "—"}</td>
                        <td style={{ padding: "8px 10px", color: COLORS.muted }}>{new Date(v.createdAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" onClick={() => onEdit(user)} style={btnPrimary()}>Edit user</button>
              <button type="button" onClick={onClose} style={btnGhost()}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AiSettingsTab() {
  const [form, setForm] = useState({
    apiKey: "",
    model: "gemini-2.5-flash-lite",
    maxOutputTokens: 1024,
    systemPrompt: "",
  });
  const [masked, setMasked] = useState("");
  const [apiKeySet, setApiKeySet] = useState(false);
  const [defaultSystemPrompt, setDefaultSystemPrompt] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.aiSettings();
      const s = data.settings;
      setForm((f) => ({
        ...f,
        model: s.model || "gemini-2.5-flash-lite",
        maxOutputTokens: s.maxOutputTokens || 1024,
        systemPrompt: s.systemPrompt || "",
        apiKey: "",
      }));
      setMasked(s.apiKeyMasked || "");
      setApiKeySet(s.apiKeySet);
      setDefaultSystemPrompt(s.defaultSystemPrompt || "");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const payload = () => ({
    apiKey: form.apiKey || undefined,
    model: form.model,
    maxOutputTokens: Number(form.maxOutputTokens),
    systemPrompt: form.systemPrompt,
  });

  const save = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await adminApi.saveAiSettings(payload());
      setMessage(data.message || "Saved");
      setForm((f) => ({ ...f, apiKey: "" }));
      setMasked(data.settings.apiKeyMasked);
      setApiKeySet(data.settings.apiKeySet);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setError("");
    setMessage("");
    try {
      const data = await adminApi.testAiSettings(payload());
      setMessage(data.message || "Connection OK");
    } catch (err) {
      setError(err.message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <p style={{ color: COLORS.muted }}>Loading AI settings…</p>;

  const sectionStyle = {
    background: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 14,
    padding: "20px 22px",
  };

  return (
    <div style={{ width: "100%" }}>
      <div style={{ ...sectionStyle, marginBottom: 20 }}>
        <h3 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
          Gemini API configuration
        </h3>
        <p style={{ color: COLORS.muted, fontSize: 14, lineHeight: 1.6, margin: 0 }}>
          Settings are stored in the database and apply immediately — no server restart needed.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 16,
          width: "100%",
          marginBottom: 16,
        }}
      >
        <div style={{ ...sectionStyle, gridColumn: "1 / -1" }}>
          <label style={{ display: "block" }}>
            <span style={{ fontSize: 12, color: COLORS.muted, display: "block", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Gemini API key
            </span>
            <input
              type="password"
              style={inputStyle()}
              placeholder={apiKeySet ? `Current: ${masked}` : "Enter your Gemini API key"}
              value={form.apiKey}
              onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
            />
            {apiKeySet && (
              <span style={{ fontSize: 12, color: COLORS.green, marginTop: 8, display: "block" }}>
                ✓ API key configured ({masked})
              </span>
            )}
          </label>
        </div>

        <div style={sectionStyle}>
          <label style={{ display: "block" }}>
            <span style={{ fontSize: 12, color: COLORS.muted, display: "block", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Model
            </span>
            <select style={inputStyle()} value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })}>
              {MODELS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>
        </div>

        <div style={sectionStyle}>
          <label style={{ display: "block" }}>
            <span style={{ fontSize: 12, color: COLORS.muted, display: "block", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Max output tokens
            </span>
            <input
              type="number"
              style={inputStyle()}
              min={256}
              max={8192}
              value={form.maxOutputTokens}
              onChange={(e) => setForm({ ...form, maxOutputTokens: e.target.value })}
            />
          </label>
        </div>

        <div style={{ ...sectionStyle, gridColumn: "1 / -1" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: COLORS.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
              System prompt
            </span>
            {defaultSystemPrompt && (
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, systemPrompt: defaultSystemPrompt }))}
                style={btnGhost()}
              >
                Reset to default
              </button>
            )}
          </div>
          <label style={{ display: "block" }}>
            <textarea
              style={{ ...inputStyle(), minHeight: 220, resize: "vertical", fontFamily: "monospace", fontSize: 12, lineHeight: 1.5 }}
              value={form.systemPrompt}
              onChange={(e) => setForm({ ...form, systemPrompt: e.target.value })}
            />
          </label>
        </div>
      </div>

      {(message || error) && (
        <div style={{ marginBottom: 16 }}>
          {message && <p style={{ color: COLORS.green, fontSize: 14, margin: 0 }}>{message}</p>}
          {error && <p style={{ color: COLORS.red, fontSize: 14, margin: message ? "8px 0 0" : 0 }}>{error}</p>}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 10,
          justifyContent: "flex-end",
          paddingTop: 4,
          borderTop: `1px solid ${COLORS.border}`,
        }}
      >
        <button type="button" onClick={test} disabled={testing} style={btnGhost()}>
          {testing ? "Testing…" : "Test connection"}
        </button>
        <button type="button" onClick={save} disabled={saving} style={btnPrimary()}>
          {saving ? "Saving…" : "Save AI settings"}
        </button>
      </div>
    </div>
  );
}

function GoogleAuthSettingsTab() {
  const [form, setForm] = useState({
    clientId: "",
    clientSecret: "",
    redirectUri: "",
  });
  const [clientSecretSet, setClientSecretSet] = useState(false);
  const [clientSecretMasked, setClientSecretMasked] = useState("");
  const [defaultRedirectUri, setDefaultRedirectUri] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.googleAuthSettings();
      const s = data.settings;
      setForm({
        clientId: s.clientId || "",
        clientSecret: "",
        redirectUri: s.redirectUri || "",
      });
      setClientSecretSet(Boolean(s.clientSecretSet));
      setClientSecretMasked(s.clientSecretMasked || "");
      setDefaultRedirectUri(s.defaultRedirectUri || "");
      setEnabled(Boolean(s.enabled));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await adminApi.saveGoogleAuthSettings({
        clientId: form.clientId.trim(),
        clientSecret: form.clientSecret || undefined,
        redirectUri: form.redirectUri.trim(),
      });
      setMessage(data.message || "Saved");
      setForm((f) => ({ ...f, clientSecret: "" }));
      setClientSecretSet(Boolean(data.settings.clientSecretSet));
      setClientSecretMasked(data.settings.clientSecretMasked || "");
      setDefaultRedirectUri(data.settings.defaultRedirectUri || "");
      setEnabled(Boolean(data.settings.enabled));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p style={{ color: COLORS.muted }}>Loading Google OAuth settings…</p>;

  const sectionStyle = {
    background: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 14,
    padding: "20px 22px",
  };

  return (
    <div style={{ width: "100%" }}>
      <div style={{ ...sectionStyle, marginBottom: 20 }}>
        <h3 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
          Google OAuth configuration
        </h3>
        <p style={{ color: COLORS.muted, fontSize: 14, lineHeight: 1.6, margin: 0 }}>
          Stored in the database and applied immediately. Create credentials in{" "}
          <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" style={{ color: COLORS.indigo }}>
            Google Cloud Console
          </a>
          .
        </p>
        <p style={{ color: enabled ? COLORS.green : COLORS.muted, fontSize: 13, margin: "12px 0 0" }}>
          {enabled ? "✓ Google sign-in is enabled" : "Google sign-in is disabled until both Client ID and Secret are set"}
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16, width: "100%", marginBottom: 16 }}>
        <div style={{ ...sectionStyle, gridColumn: "1 / -1" }}>
          <label style={{ display: "block" }}>
            <span style={{ fontSize: 12, color: COLORS.muted, display: "block", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Client ID
            </span>
            <input
              type="text"
              style={inputStyle()}
              placeholder="123456789-abc.apps.googleusercontent.com"
              value={form.clientId}
              onChange={(e) => setForm({ ...form, clientId: e.target.value })}
            />
          </label>
        </div>

        <div style={{ ...sectionStyle, gridColumn: "1 / -1" }}>
          <label style={{ display: "block" }}>
            <span style={{ fontSize: 12, color: COLORS.muted, display: "block", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Client secret
            </span>
            <input
              type="password"
              style={inputStyle()}
              placeholder={clientSecretSet ? `Current: ${clientSecretMasked}` : "Enter your Google client secret"}
              value={form.clientSecret}
              onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
            />
            {clientSecretSet && (
              <span style={{ fontSize: 12, color: COLORS.green, marginTop: 8, display: "block" }}>
                ✓ Client secret configured ({clientSecretMasked})
              </span>
            )}
          </label>
        </div>

        <div style={{ ...sectionStyle, gridColumn: "1 / -1" }}>
          <label style={{ display: "block" }}>
            <span style={{ fontSize: 12, color: COLORS.muted, display: "block", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Redirect URI (optional)
            </span>
            <input
              type="text"
              style={inputStyle()}
              placeholder={defaultRedirectUri || "http://localhost:4000/api/auth/google/callback"}
              value={form.redirectUri}
              onChange={(e) => setForm({ ...form, redirectUri: e.target.value })}
            />
            <span style={{ fontSize: 12, color: COLORS.muted, marginTop: 8, display: "block", lineHeight: 1.5 }}>
              Leave blank to use the default: <code style={{ color: COLORS.text }}>{defaultRedirectUri}</code>
              <br />
              Add this exact URI as an authorized redirect URI in Google Cloud Console.
            </span>
          </label>
        </div>
      </div>

      {(message || error) && (
        <div style={{ marginBottom: 16 }}>
          {message && <p style={{ color: COLORS.green, fontSize: 14, margin: 0 }}>{message}</p>}
          {error && <p style={{ color: COLORS.red, fontSize: 14, margin: message ? "8px 0 0" : 0 }}>{error}</p>}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 10,
          justifyContent: "flex-end",
          paddingTop: 4,
          borderTop: `1px solid ${COLORS.border}`,
        }}
      >
        <button type="button" onClick={save} disabled={saving} style={btnPrimary()}>
          {saving ? "Saving…" : "Save Google OAuth settings"}
        </button>
      </div>
    </div>
  );
}

function StripeSettingsTab() {
  const [form, setForm] = useState({
    secretKey: "",
    publishableKey: "",
    priceStarter: "",
    pricePro: "",
    priceTeam: "",
    demoPurchases: false,
  });
  const [secretKeySet, setSecretKeySet] = useState(false);
  const [secretKeyMasked, setSecretKeyMasked] = useState("");
  const [checkoutReady, setCheckoutReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.stripeSettings();
      const s = data.settings;
      setForm({
        secretKey: "",
        publishableKey: s.publishableKey || "",
        priceStarter: s.priceStarter || "",
        pricePro: s.pricePro || "",
        priceTeam: s.priceTeam || "",
        demoPurchases: Boolean(s.demoPurchases),
      });
      setSecretKeySet(Boolean(s.secretKeySet));
      setSecretKeyMasked(s.secretKeyMasked || "");
      setCheckoutReady(Boolean(s.checkoutReady));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const payload = () => ({
    secretKey: form.secretKey || undefined,
    publishableKey: form.publishableKey.trim(),
    priceStarter: form.priceStarter.trim(),
    pricePro: form.pricePro.trim(),
    priceTeam: form.priceTeam.trim(),
    demoPurchases: form.demoPurchases,
  });

  const save = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await adminApi.saveStripeSettings(payload());
      setMessage(data.message || "Saved");
      setForm((f) => ({ ...f, secretKey: "" }));
      setSecretKeySet(Boolean(data.settings.secretKeySet));
      setSecretKeyMasked(data.settings.secretKeyMasked || "");
      setCheckoutReady(Boolean(data.settings.checkoutReady));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setError("");
    setMessage("");
    try {
      const data = await adminApi.testStripeSettings({ secretKey: form.secretKey || undefined });
      setMessage(data.message || "Connection OK");
    } catch (err) {
      setError(err.message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <p style={{ color: COLORS.muted }}>Loading Stripe settings…</p>;

  const sectionStyle = {
    background: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 14,
    padding: "20px 22px",
  };

  const planFields = [
    { key: "priceStarter", label: "Starter price ID ($9/mo)", placeholder: "price_..." },
    { key: "pricePro", label: "Pro price ID ($29/mo)", placeholder: "price_..." },
    { key: "priceTeam", label: "Team price ID ($99/mo)", placeholder: "price_..." },
  ];

  return (
    <div style={{ width: "100%" }}>
      <div style={{ ...sectionStyle, marginBottom: 20 }}>
        <h3 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
          Stripe payments
        </h3>
        <p style={{ color: COLORS.muted, fontSize: 14, lineHeight: 1.6, margin: 0 }}>
          In-app card payments via Stripe Elements (no Stripe-hosted checkout page, no webhooks).
          Plans activate immediately after successful payment. Create recurring prices in{" "}
          <a href="https://dashboard.stripe.com/products" target="_blank" rel="noreferrer" style={{ color: COLORS.indigo }}>
            Stripe Dashboard
          </a>
          .
        </p>
        <p style={{ color: checkoutReady ? COLORS.green : COLORS.muted, fontSize: 13, margin: "12px 0 0" }}>
          {checkoutReady
            ? "✓ In-app payments are live"
            : form.demoPurchases
              ? "Demo mode — plans activate without payment (testing only)"
              : "Payments disabled — add secret key, publishable key, and all price IDs"}
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16, width: "100%", marginBottom: 16 }}>
        <div style={{ ...sectionStyle, gridColumn: "1 / -1" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={form.demoPurchases}
              onChange={(e) => setForm({ ...form, demoPurchases: e.target.checked })}
            />
            <span style={{ fontSize: 14, color: COLORS.text }}>Demo purchases (skip payment — testing only)</span>
          </label>
        </div>

        <div style={{ ...sectionStyle, gridColumn: "1 / -1" }}>
          <label style={{ display: "block" }}>
            <span style={{ fontSize: 12, color: COLORS.muted, display: "block", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Publishable key
            </span>
            <input
              type="text"
              style={inputStyle()}
              placeholder="pk_live_... or pk_test_..."
              value={form.publishableKey}
              onChange={(e) => setForm({ ...form, publishableKey: e.target.value })}
            />
          </label>
        </div>

        <div style={{ ...sectionStyle, gridColumn: "1 / -1" }}>
          <label style={{ display: "block" }}>
            <span style={{ fontSize: 12, color: COLORS.muted, display: "block", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Secret key
            </span>
            <input
              type="password"
              style={inputStyle()}
              placeholder={secretKeySet ? `Current: ${secretKeyMasked}` : "sk_live_... or sk_test_..."}
              value={form.secretKey}
              onChange={(e) => setForm({ ...form, secretKey: e.target.value })}
            />
          </label>
        </div>

        {planFields.map((field) => (
          <div key={field.key} style={{ ...sectionStyle, gridColumn: "1 / -1" }}>
            <label style={{ display: "block" }}>
              <span style={{ fontSize: 12, color: COLORS.muted, display: "block", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {field.label}
              </span>
              <input
                type="text"
                style={inputStyle()}
                placeholder={field.placeholder}
                value={form[field.key]}
                onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
              />
            </label>
          </div>
        ))}
      </div>

      {(message || error) && (
        <div style={{ marginBottom: 16 }}>
          {message && <p style={{ color: COLORS.green, fontSize: 14, margin: 0 }}>{message}</p>}
          {error && <p style={{ color: COLORS.red, fontSize: 14, margin: message ? "8px 0 0" : 0 }}>{error}</p>}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 10,
          justifyContent: "flex-end",
          paddingTop: 4,
          borderTop: `1px solid ${COLORS.border}`,
        }}
      >
        <button type="button" onClick={test} disabled={testing} style={btnGhost()}>
          {testing ? "Testing…" : "Test connection"}
        </button>
        <button type="button" onClick={save} disabled={saving} style={btnPrimary()}>
          {saving ? "Saving…" : "Save Stripe settings"}
        </button>
      </div>
    </div>
  );
}

function BackupSettingsTab() {
  const [form, setForm] = useState({
    accessKeyId: "",
    secretAccessKey: "",
    region: "eu-north-1",
    bucket: "ai-startup-validator-backup",
    prefix: "aistartupvalidator/backups/",
    retentionDays: 30,
  });
  const [secretSet, setSecretSet] = useState(false);
  const [secretMasked, setSecretMasked] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [lastRun, setLastRun] = useState("");
  const [lastStatus, setLastStatus] = useState("");
  const [lastMessage, setLastMessage] = useState("");
  const [s3LatestPath, setS3LatestPath] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const applySettings = (s) => {
    setForm({
      accessKeyId: s.accessKeyId || "",
      secretAccessKey: "",
      region: s.region || "eu-north-1",
      bucket: s.bucket || "ai-startup-validator-backup",
      prefix: s.prefix || "aistartupvalidator/backups/",
      retentionDays: s.retentionDays ?? 30,
    });
    setSecretSet(Boolean(s.secretAccessKeySet));
    setSecretMasked(s.secretAccessKeyMasked || "");
    setEnabled(Boolean(s.enabled));
    setLastRun(s.lastRun || "");
    setLastStatus(s.lastStatus || "");
    setLastMessage(s.lastMessage || "");
    setS3LatestPath(s.s3LatestPath || "");
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.backupSettings();
      applySettings(data.settings);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const payload = () => ({
    accessKeyId: form.accessKeyId.trim(),
    secretAccessKey: form.secretAccessKey || undefined,
    region: form.region.trim(),
    bucket: form.bucket.trim(),
    prefix: form.prefix.trim(),
    retentionDays: Number(form.retentionDays),
  });

  const save = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await adminApi.saveBackupSettings(payload());
      applySettings(data.settings);
      setForm((f) => ({ ...f, secretAccessKey: "" }));
      setMessage(data.message || "Saved");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setError("");
    setMessage("");
    try {
      const data = await adminApi.testBackupSettings(payload());
      setMessage(data.message || "Connection OK");
    } catch (err) {
      setError(err.message);
    } finally {
      setTesting(false);
    }
  };

  const runBackup = async () => {
    setRunning(true);
    setError("");
    setMessage("");
    try {
      const data = await adminApi.runBackup(payload());
      applySettings(data.settings);
      setForm((f) => ({ ...f, secretAccessKey: "" }));
      setMessage(data.message || "Backup completed");
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <p style={{ color: COLORS.muted }}>Loading backup settings…</p>;

  const sectionStyle = {
    background: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 14,
    padding: "20px 22px",
  };

  return (
    <div style={{ width: "100%" }}>
      <div style={{ ...sectionStyle, marginBottom: 20 }}>
        <h3 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
          AWS S3 database backup
        </h3>
        <p style={{ color: COLORS.muted, fontSize: 14, lineHeight: 1.6, margin: "8px 0 0" }}>
          Region must match the bucket exactly (S3 Console → bucket → Properties → AWS Region).
          This bucket is in <strong style={{ color: COLORS.text }}>eu-north-1</strong> (Stockholm).
          IAM user needs HeadBucket, ListBucket, PutObject, GetObject, DeleteObject — see{" "}
          <code style={{ color: COLORS.text }}>backend/docs/S3-BACKUP-GUIDE.md</code>.
        </p>
        <p style={{ color: enabled ? COLORS.green : COLORS.muted, fontSize: 13, margin: "12px 0 0" }}>
          {enabled ? "✓ S3 backup is configured" : "Add access key, secret, region, and bucket to enable backups"}
        </p>
        {lastRun && (
          <p style={{ color: COLORS.muted, fontSize: 13, margin: "8px 0 0", lineHeight: 1.5 }}>
            Last run: {new Date(lastRun).toLocaleString()}
            {lastStatus && (
              <span style={{ color: lastStatus === "success" ? COLORS.green : COLORS.red, marginLeft: 8 }}>
                ({lastStatus})
              </span>
            )}
            {lastMessage && <span style={{ display: "block", marginTop: 4 }}>{lastMessage}</span>}
            {s3LatestPath && <span style={{ display: "block", marginTop: 4 }}>Latest: {s3LatestPath}</span>}
          </p>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16, marginBottom: 16 }}>
        {[
          { key: "accessKeyId", label: "AWS access key ID", type: "text", placeholder: "AKIA..." },
          { key: "secretAccessKey", label: "AWS secret access key", type: "password", placeholder: secretSet ? `Current: ${secretMasked}` : "Secret key" },
          { key: "region", label: "AWS region", type: "text", placeholder: "eu-north-1" },
          { key: "bucket", label: "S3 bucket", type: "text", placeholder: "ai-startup-validator-backup" },
          { key: "prefix", label: "S3 prefix (folder)", type: "text", placeholder: "aistartupvalidator/backups/" },
          { key: "retentionDays", label: "Retention (days)", type: "number", placeholder: "30" },
        ].map((field) => (
          <div key={field.key} style={{ ...sectionStyle, gridColumn: field.key === "prefix" ? "1 / -1" : undefined }}>
            <label style={{ display: "block" }}>
              <span style={{ fontSize: 12, color: COLORS.muted, display: "block", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {field.label}
              </span>
              <input
                type={field.type}
                style={inputStyle()}
                placeholder={field.placeholder}
                value={form[field.key]}
                onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
              />
            </label>
          </div>
        ))}
      </div>

      {(message || error) && (
        <div style={{ marginBottom: 16 }}>
          {message && <p style={{ color: COLORS.green, fontSize: 14, margin: 0 }}>{message}</p>}
          {error && <p style={{ color: COLORS.red, fontSize: 14, margin: message ? "8px 0 0" : 0 }}>{error}</p>}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 10,
          justifyContent: "flex-end",
          flexWrap: "wrap",
          paddingTop: 4,
          borderTop: `1px solid ${COLORS.border}`,
        }}
      >
        <button type="button" onClick={test} disabled={testing} style={btnGhost()}>
          {testing ? "Testing…" : "Test S3 connection"}
        </button>
        <button type="button" onClick={runBackup} disabled={running} style={btnGhost()}>
          {running ? "Backing up…" : "Run backup now"}
        </button>
        <button type="button" onClick={save} disabled={saving} style={btnPrimary()}>
          {saving ? "Saving…" : "Save backup settings"}
        </button>
      </div>
    </div>
  );
}

function AdminsTab({ currentUserEmail }) {
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [storedInDatabase, setStoredInDatabase] = useState(false);
  const [envFallback, setEnvFallback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.admins();
      setEmails(data.emails || []);
      setStoredInDatabase(Boolean(data.storedInDatabase));
      setEnvFallback(data.envFallback || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Invalid email address");
      return;
    }
    if (emails.includes(email)) {
      setError("Email already in the list");
      return;
    }
    setEmails([...emails, email]);
    setNewEmail("");
    setError("");
  };

  const removeEmail = (email) => {
    if (email === currentUserEmail?.toLowerCase() && emails.length <= 1) {
      setError("You must keep at least one admin email, including your own");
      return;
    }
    if (email === currentUserEmail?.toLowerCase()) {
      setError("You cannot remove your own admin access");
      return;
    }
    setEmails(emails.filter((e) => e !== email));
    setError("");
  };

  const save = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await adminApi.saveAdmins(emails);
      setEmails(data.emails);
      setStoredInDatabase(true);
      setMessage(data.message || "Saved");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p style={{ color: COLORS.muted }}>Loading admin access…</p>;

  const sectionStyle = {
    background: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 14,
    padding: "20px 22px",
  };

  return (
    <div style={{ width: "100%" }}>
      <div style={{ ...sectionStyle, marginBottom: 20 }}>
        <h3 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
          Admin access
        </h3>
        <p style={{ color: COLORS.muted, fontSize: 14, lineHeight: 1.6, margin: 0 }}>
          Users with these email addresses can open the Admin Console. Changes apply immediately — no server restart.
        </p>
        <p style={{ color: storedInDatabase ? COLORS.green : COLORS.muted, fontSize: 13, margin: "12px 0 0" }}>
          {storedInDatabase
            ? "✓ Admin emails stored in database"
            : `Using environment fallback: ${envFallback.join(", ")}`}
        </p>
      </div>

      <div style={{ ...sectionStyle, marginBottom: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          {emails.map((email) => (
            <div
              key={email}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${COLORS.border}`,
                background: "rgba(255,255,255,0.02)",
              }}
            >
              <span style={{ fontSize: 14 }}>
                {email}
                {email === currentUserEmail?.toLowerCase() && (
                  <span style={{ color: COLORS.muted, fontSize: 12, marginLeft: 8 }}>(you)</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => removeEmail(email)}
                style={{ ...btnGhost(), padding: "4px 10px", color: COLORS.red, borderColor: "rgba(239,68,68,0.3)" }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input
            style={{ ...inputStyle(), flex: 1 }}
            placeholder="Add admin email…"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())}
          />
          <button type="button" onClick={addEmail} style={btnGhost()}>
            Add
          </button>
        </div>
      </div>

      {(message || error) && (
        <div style={{ marginBottom: 16 }}>
          {message && <p style={{ color: COLORS.green, fontSize: 14, margin: 0 }}>{message}</p>}
          {error && <p style={{ color: COLORS.red, fontSize: 14, margin: message ? "8px 0 0" : 0 }}>{error}</p>}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="button" onClick={save} disabled={saving} style={btnPrimary()}>
          {saving ? "Saving…" : "Save admin emails"}
        </button>
      </div>
    </div>
  );
}

function bugStatusColor(status) {
  if (status === "open") return COLORS.red;
  if (status === "in_progress") return COLORS.amber;
  if (status === "resolved") return COLORS.green;
  return COLORS.muted;
}

function bugStatusLabel(status) {
  if (!status) return "—";
  return status.replace(/_/g, " ");
}

function formatDateTime(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

function AiUsageTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [days, setDays] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.aiUsage(days || undefined);
      setData(res);
    } catch (err) {
      setError(err.message || "Failed to load AI usage");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <select style={{ ...inputStyle(), maxWidth: 220 }} value={days} onChange={(e) => setDays(e.target.value)}>
          <option value="">All time</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
        <button type="button" onClick={load} style={btnGhost()} disabled={loading}>
          Refresh
        </button>
      </div>

      {error && <p style={{ color: COLORS.red, fontSize: 14, marginBottom: 16 }}>{error}</p>}
      {loading && <p style={{ color: COLORS.muted }}>Loading AI usage…</p>}

      {!loading && data && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 24 }}>
            <StatCard label="Requests" value={data.totals.requestCount} />
            <StatCard label="Input tokens" value={formatTokens(data.totals.promptTokens)} />
            <StatCard label="Output tokens" value={formatTokens(data.totals.completionTokens)} />
            <StatCard label="Total tokens" value={formatTokens(data.totals.totalTokens)} />
            <StatCard label="Est. cost" value={formatUsd(data.totals.estimatedCostUsd)} />
          </div>

          <h4 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 15, marginBottom: 10 }}>By operation</h4>
          {data.byOperation?.length === 0 ? (
            <p style={{ color: COLORS.muted, fontSize: 13, marginBottom: 24 }}>No AI usage logged yet.</p>
          ) : (
            <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 12, marginBottom: 24 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                    {["Operation", "Requests", "Tokens", "Est. cost"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "10px 12px", color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.byOperation.map((row) => (
                    <tr key={row.operation} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: "10px 12px" }}>{row.label}</td>
                      <td style={{ padding: "10px 12px" }}>{row.requestCount}</td>
                      <td style={{ padding: "10px 12px" }}>{formatTokens(row.totalTokens)}</td>
                      <td style={{ padding: "10px 12px" }}>{formatUsd(row.estimatedCostUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h4 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 15, marginBottom: 10 }}>Top users by tokens</h4>
          {data.topUsers?.length === 0 ? (
            <p style={{ color: COLORS.muted, fontSize: 13 }}>No user usage yet.</p>
          ) : (
            <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                    {["User", "Email", "Requests", "Tokens", "Est. cost"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "10px 12px", color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.topUsers.map((row) => (
                    <tr key={row.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: "10px 12px" }}>{row.name}</td>
                      <td style={{ padding: "10px 12px", color: COLORS.muted }}>{row.email}</td>
                      <td style={{ padding: "10px 12px" }}>{row.requestCount}</td>
                      <td style={{ padding: "10px 12px" }}>{formatTokens(row.totalTokens)}</td>
                      <td style={{ padding: "10px 12px" }}>{formatUsd(row.estimatedCostUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p style={{ color: COLORS.muted, fontSize: 12, marginTop: 16, lineHeight: 1.5 }}>
            Costs are estimates based on Gemini list pricing per model. Usage is recorded for validations, development tools, and admin AI tests.
          </p>
        </>
      )}
    </div>
  );
}

function BugReportsTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reports, setReports] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [editStatus, setEditStatus] = useState("open");
  const [editNotes, setEditNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await adminApi.bugReports({ status: statusFilter || undefined });
      setReports(data.reports || []);
    } catch (err) {
      setError(err.message || "Failed to load bug reports");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const openReport = (report) => {
    setSelected(report);
    setEditStatus(report.status);
    setEditNotes(report.adminNotes || "");
  };

  const saveReport = async () => {
    if (!selected) return;
    setSaving(true);
    setError("");
    try {
      const data = await adminApi.updateBugReport(selected.id, {
        status: editStatus,
        adminNotes: editNotes,
      });
      setSelected(data.report);
      await load();
    } catch (err) {
      setError(err.message || "Failed to update bug report");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: `1px solid ${COLORS.border}`,
            background: "#0A0F1C",
            color: COLORS.text,
            fontSize: 13,
          }}
        >
          {BUG_STATUSES.map((s) => (
            <option key={s || "all"} value={s}>
              {s ? bugStatusLabel(s) : "All statuses"}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={load}
          style={{
            padding: "8px 14px",
            borderRadius: 8,
            border: `1px solid ${COLORS.border}`,
            background: "transparent",
            color: COLORS.text,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Refresh
        </button>
      </div>

      {error && (
        <div style={{ color: COLORS.red, fontSize: 14, marginBottom: 16, padding: "10px 12px", background: "rgba(239,68,68,0.1)", borderRadius: 10 }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ color: COLORS.muted }}>Loading bug reports…</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: selected ? "1fr 360px" : "1fr", gap: 16 }}>
          <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                  {["ID", "User", "Description", "Status", "Submitted", ""].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "10px 12px", color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reports.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: 20, textAlign: "center", color: COLORS.muted }}>
                      No bug reports yet.
                    </td>
                  </tr>
                ) : (
                  reports.map((r) => (
                    <tr key={r.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: "10px 12px" }}>#{r.id}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <div>{r.userName}</div>
                        <div style={{ color: COLORS.muted, fontSize: 12 }}>{r.userEmail}</div>
                      </td>
                      <td style={{ padding: "10px 12px", maxWidth: 280 }}>
                        {r.description.length > 80 ? `${r.description.slice(0, 80)}…` : r.description}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ color: bugStatusColor(r.status), textTransform: "capitalize" }}>
                          {bugStatusLabel(r.status)}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px", color: COLORS.muted, whiteSpace: "nowrap" }}>
                        {formatDateTime(r.createdAt)}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <button
                          type="button"
                          onClick={() => openReport(r)}
                          style={{
                            padding: "6px 10px",
                            borderRadius: 8,
                            border: `1px solid ${COLORS.border}`,
                            background: selected?.id === r.id ? "rgba(99,102,241,0.15)" : "transparent",
                            color: COLORS.text,
                            fontSize: 12,
                            cursor: "pointer",
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {selected && (
            <div
              style={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: 12,
                padding: 16,
                alignSelf: "start",
                position: "sticky",
                top: 0,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontFamily: "Space Grotesk, sans-serif" }}>Report #{selected.id}</h3>
                <button type="button" onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: COLORS.muted, cursor: "pointer" }}>
                  ×
                </button>
              </div>

              <p style={{ fontSize: 12, color: COLORS.muted, margin: "0 0 12px" }}>
                {selected.userName} · {selected.userEmail} · {planLabel(selected.userPlan)}
              </p>

              <div style={{ fontSize: 13, marginBottom: 12 }}>
                <div style={{ color: COLORS.muted, marginBottom: 4 }}>Description</div>
                <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{selected.description}</div>
              </div>

              {selected.steps && (
                <div style={{ fontSize: 13, marginBottom: 12 }}>
                  <div style={{ color: COLORS.muted, marginBottom: 4 }}>Steps to reproduce</div>
                  <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{selected.steps}</div>
                </div>
              )}

              {selected.pageUrl && (
                <div style={{ fontSize: 12, marginBottom: 12, color: COLORS.muted, wordBreak: "break-all" }}>
                  Page: {selected.pageUrl}
                </div>
              )}

              {selected.userAgent && (
                <div style={{ fontSize: 11, marginBottom: 16, color: COLORS.muted, wordBreak: "break-all" }}>
                  {selected.userAgent}
                </div>
              )}

              <label style={{ display: "block", fontSize: 12, color: COLORS.muted, marginBottom: 6 }}>Status</label>
              <select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: `1px solid ${COLORS.border}`,
                  background: "#0A0F1C",
                  color: COLORS.text,
                  fontSize: 13,
                  marginBottom: 12,
                }}
              >
                {BUG_STATUSES.filter(Boolean).map((s) => (
                  <option key={s} value={s}>
                    {bugStatusLabel(s)}
                  </option>
                ))}
              </select>

              <label style={{ display: "block", fontSize: 12, color: COLORS.muted, marginBottom: 6 }}>Admin notes</label>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={4}
                placeholder="Internal notes..."
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: `1px solid ${COLORS.border}`,
                  background: "rgba(255,255,255,0.04)",
                  color: COLORS.text,
                  fontSize: 13,
                  resize: "vertical",
                  marginBottom: 12,
                  boxSizing: "border-box",
                }}
              />

              <button
                type="button"
                onClick={saveReport}
                disabled={saving}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: "none",
                  background: COLORS.indigo,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: saving ? "wait" : "pointer",
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminPanel({ onClose, currentUserId, currentUserEmail }) {
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [validations, setValidations] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [search, setSearch] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [purchaseStatus, setPurchaseStatus] = useState("");
  const [purchasePlan, setPurchasePlan] = useState("");
  const [refundingId, setRefundingId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const loadUsers = useCallback(async (q = "") => {
    const data = await adminApi.users(q);
    setUsers(data.users);
  }, []);

  const loadSubscriptions = useCallback(async () => {
    const data = await adminApi.subscriptions();
    setSubscriptions(data.subscriptions);
  }, []);

  const loadOverview = useCallback(async () => {
    const [statsRes, validationsRes] = await Promise.all([adminApi.stats(), adminApi.validations()]);
    setStats(statsRes.stats);
    setValidations(validationsRes.validations);
  }, []);

  const loadPurchases = useCallback(async (status = "", plan = "") => {
    const data = await adminApi.purchases({
      status: status || undefined,
      plan: plan || undefined,
    });
    setPurchases(data.purchases);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (tab === "users") await loadUsers(search);
      else if (tab === "subscriptions") await loadSubscriptions();
      else if (tab === "purchases") await loadPurchases(purchaseStatus, purchasePlan);
      else if (tab !== "ai" && tab !== "google" && tab !== "stripe" && tab !== "backup" && tab !== "admins" && tab !== "bugs" && tab !== "usage") await loadOverview();
      if (tab === "overview") await loadUsers();
    } catch (err) {
      setError(err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [tab, search, purchaseStatus, purchasePlan, loadUsers, loadOverview, loadSubscriptions, loadPurchases]);

  useEffect(() => {
    if (tab === "ai" || tab === "google" || tab === "stripe" || tab === "backup" || tab === "admins" || tab === "bugs" || tab === "usage") return;
    const t = setTimeout(load, tab === "users" ? 300 : 0);
    return () => clearTimeout(t);
  }, [tab, search, load]);

  const refundPurchase = async (purchase) => {
    if (
      !window.confirm(
        `Issue a full Stripe refund for ${purchase.userName} (${planLabel(purchase.plan)}, ${formatMoney(purchase.amountCents)})? This also cancels any linked subscription.`
      )
    ) {
      return;
    }
    setRefundingId(purchase.id);
    setError("");
    try {
      await adminApi.refundPurchase(purchase.id);
      await loadPurchases(purchaseStatus, purchasePlan);
    } catch (err) {
      setError(err.message);
    } finally {
      setRefundingId(null);
    }
  };

  const exportCsv = async (type) => {
    setExporting(true);
    setError("");
    try {
      if (type === "users") await adminApi.exportUsersCsv();
      else await adminApi.exportPurchasesCsv({ status: purchaseStatus, plan: purchasePlan });
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Delete ${user.email}? This cannot be undone.`)) return;
    try {
      await adminApi.deleteUser(user.id);
      await loadUsers(search);
    } catch (err) {
      setError(err.message);
    }
  };

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "users", label: "Users" },
    { id: "usage", label: "AI Usage" },
    { id: "subscriptions", label: "Subscriptions" },
    { id: "validations", label: "Validations" },
    { id: "purchases", label: "Purchases" },
    { id: "bugs", label: "Bug reports" },
    { id: "ai", label: "AI Settings" },
    { id: "google", label: "Google OAuth" },
    { id: "stripe", label: "Stripe" },
    { id: "backup", label: "Backup" },
    { id: "admins", label: "Admins" },
  ];

  const formatMoney = (cents) => `$${(cents / 100).toFixed(2)}`;

  return (
    <PanelShell title="Admin Console" onClose={onClose}>
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={{
              padding: "8px 14px",
              borderRadius: 99,
              border: `1px solid ${tab === t.id ? COLORS.indigo : COLORS.border}`,
              background: tab === t.id ? "rgba(99,102,241,0.15)" : "transparent",
              color: tab === t.id ? COLORS.text : COLORS.muted,
              fontSize: 13,
              fontWeight: tab === t.id ? 600 : 400,
              cursor: "pointer",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ color: COLORS.red, fontSize: 14, marginBottom: 16, padding: "10px 12px", background: "rgba(239,68,68,0.1)", borderRadius: 10 }}>
          {error}
        </div>
      )}

      {tab === "ai" && <AiSettingsTab />}
      {tab === "google" && <GoogleAuthSettingsTab />}
      {tab === "stripe" && <StripeSettingsTab />}
      {tab === "backup" && <BackupSettingsTab />}
      {tab === "admins" && <AdminsTab currentUserEmail={currentUserEmail} />}
      {tab === "bugs" && <BugReportsTab />}
      {tab === "usage" && <AiUsageTab />}

      {tab === "users" && (
        <>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            <input
              style={{ ...inputStyle(), maxWidth: 360, flex: 1 }}
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button type="button" onClick={() => exportCsv("users")} disabled={exporting} style={btnGhost()}>
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
          </div>
          {loading ? (
            <p style={{ color: COLORS.muted }}>Loading users…</p>
          ) : (
            <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                    {["Name", "Email", "Plan", "Credits", "Stripe", "Validations", "AI tokens", "Est. cost", "Joined", ""].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "10px 12px", color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: "10px 12px" }}>{u.name}</td>
                      <td style={{ padding: "10px 12px", color: COLORS.muted }}>{u.email}</td>
                      <td style={{ padding: "10px 12px" }}>{planLabel(u.plan)}</td>
                      <td style={{ padding: "10px 12px" }}>{u.credits}</td>
                      <td style={{ padding: "10px 12px" }}>
                        {u.stripeSubscriptionId ? (
                          <span style={{ fontSize: 12, color: COLORS.green }}>Sub linked</span>
                        ) : u.stripeCustomerId ? (
                          <span style={{ fontSize: 12, color: COLORS.muted }}>Customer</span>
                        ) : (
                          <span style={{ color: COLORS.muted }}>—</span>
                        )}
                        <div style={{ marginTop: 4 }}>
                          <StripeLinks user={u} />
                        </div>
                      </td>
                      <td style={{ padding: "10px 12px" }}>{u.validationCount ?? 0}</td>
                      <td style={{ padding: "10px 12px" }} title={`${u.aiPromptTokens ?? 0} in / ${u.aiCompletionTokens ?? 0} out`}>
                        {formatTokens(u.aiTotalTokens ?? 0)}
                        {(u.aiRequestCount ?? 0) > 0 && (
                          <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>{u.aiRequestCount} req</div>
                        )}
                      </td>
                      <td style={{ padding: "10px 12px" }}>{formatUsd(u.aiEstimatedCostUsd ?? 0)}</td>
                      <td style={{ padding: "10px 12px", color: COLORS.muted }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                      <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                        <button type="button" onClick={() => setDetailUser(u)} style={{ ...btnGhost(), padding: "6px 10px", marginRight: 6 }}>View</button>
                        <button type="button" onClick={() => setEditUser(u)} style={{ ...btnGhost(), padding: "6px 10px", marginRight: 6 }}>Edit</button>
                        {u.id !== currentUserId && (
                          <button
                            type="button"
                            onClick={() => deleteUser(u)}
                            style={{ ...btnGhost(), padding: "6px 10px", color: COLORS.red, borderColor: "rgba(239,68,68,0.3)" }}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "overview" && loading && <p style={{ color: COLORS.muted }}>Loading overview…</p>}

      {tab === "overview" && !loading && stats && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 24 }}>
            <StatCard label="Users" value={stats.totalUsers} />
            <StatCard label="Active subscribers" value={stats.activeSubscribers} />
            <StatCard label="MRR" value={formatMoney(stats.mrrCents)} />
            <StatCard label="Stripe linked" value={stats.stripeLinkedSubscribers} />
            <StatCard label="Validations" value={stats.totalValidations} />
            <StatCard label="Purchases" value={stats.totalPurchases} />
            <StatCard label="Revenue (all time)" value={formatMoney(stats.revenueCents)} />
            <StatCard label="Open bugs" value={stats.openBugReports ?? 0} />
            <StatCard label="AI tokens (30d)" value={formatTokens(stats.aiUsageLast30Days?.totalTokens ?? 0)} />
            <StatCard label="AI cost (30d)" value={formatUsd(stats.aiUsageLast30Days?.estimatedCostUsd ?? 0)} />
            <StatCard label="AI tokens (all)" value={formatTokens(stats.aiUsageAllTime?.totalTokens ?? 0)} />
            <StatCard label="AI cost (all)" value={formatUsd(stats.aiUsageAllTime?.estimatedCostUsd ?? 0)} />
          </div>
          <p style={{ color: COLORS.muted, fontSize: 12, margin: "0 0 16px" }}>
            MRR is estimated from current paid plans in the app (Starter $9 + Pro $29 + Team $99 / mo).
            AI cost is estimated from Gemini public pricing per model; logged from the next request onward.
          </p>
          {stats.planBreakdown?.length > 0 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {stats.planBreakdown.map((p) => (
                <span key={p.plan} style={{ padding: "6px 12px", borderRadius: 99, background: COLORS.surface, border: `1px solid ${COLORS.border}`, fontSize: 13 }}>
                  {planLabel(p.plan)}: <strong>{p.count}</strong>
                </span>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "subscriptions" && loading && <p style={{ color: COLORS.muted }}>Loading subscriptions…</p>}

      {tab === "subscriptions" && !loading && (
        <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                {["User", "Email", "App plan", "Stripe status", "Renews / ends", "Stripe", ""].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "10px 12px", color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {subscriptions.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: 20, textAlign: "center", color: COLORS.muted }}>
                    No paid users or Stripe subscriptions yet.
                  </td>
                </tr>
              ) : (
                subscriptions.map((row) => {
                  const subStatus = row.stripe?.subscription?.status;
                  return (
                    <tr key={row.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: "10px 12px" }}>{row.name}</td>
                      <td style={{ padding: "10px 12px", color: COLORS.muted }}>{row.email}</td>
                      <td style={{ padding: "10px 12px" }}>{planLabel(row.plan)}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ color: stripeStatusColor(subStatus), fontWeight: 600 }}>
                          {row.stripe?.subscriptionId ? formatStripeStatus(subStatus || "linked") : "—"}
                        </span>
                        {row.stripe?.subscription?.planId && row.stripe.subscription.planId !== row.plan && (
                          <div style={{ fontSize: 11, color: "#F59E0B", marginTop: 4 }}>
                            Stripe plan: {planLabel(row.stripe.subscription.planId)}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "10px 12px", color: COLORS.muted }}>
                        {formatPeriodEnd(row.stripe?.subscription?.currentPeriodEnd)}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <StripeLinks
                          user={{
                            stripeCustomerUrl: row.stripe?.customerUrl,
                            stripeSubscriptionUrl: row.stripe?.subscriptionUrl,
                            stripeCustomerId: row.stripe?.customerId,
                            stripeSubscriptionId: row.stripe?.subscriptionId,
                          }}
                        />
                      </td>
                      <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                        <button
                          type="button"
                          onClick={() => setDetailUser(row)}
                          style={{ ...btnGhost(), padding: "6px 10px", marginRight: 6 }}
                        >
                          View
                        </button>
                        <button type="button" onClick={() => setEditUser(row)} style={{ ...btnGhost(), padding: "6px 10px" }}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "validations" && loading && <p style={{ color: COLORS.muted }}>Loading validations…</p>}

      {tab === "validations" && !loading && (
        <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                {["User", "Idea", "Verdict", "Score", "Date"].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "10px 12px", color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {validations.map((v) => (
                <tr key={v.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: "10px 12px" }}>{v.user_name}</td>
                  <td style={{ padding: "10px 12px", color: COLORS.muted, maxWidth: 280 }}>{v.idea?.length > 60 ? `${v.idea.slice(0, 60)}…` : v.idea}</td>
                  <td style={{ padding: "10px 12px" }}>{v.verdict}</td>
                  <td style={{ padding: "10px 12px" }}>{v.overall_score}</td>
                  <td style={{ padding: "10px 12px", color: COLORS.muted }}>{new Date(v.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "purchases" && (
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <select
            style={{ ...inputStyle(), width: "auto", minWidth: 140 }}
            value={purchaseStatus}
            onChange={(e) => setPurchaseStatus(e.target.value)}
          >
            <option value="">All statuses</option>
            {PURCHASE_STATUSES.filter(Boolean).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            style={{ ...inputStyle(), width: "auto", minWidth: 140 }}
            value={purchasePlan}
            onChange={(e) => setPurchasePlan(e.target.value)}
          >
            <option value="">All plans</option>
            {PLANS.filter((p) => p !== "free").map((p) => (
              <option key={p} value={p}>{planLabel(p)}</option>
            ))}
          </select>
          <button type="button" onClick={() => exportCsv("purchases")} disabled={exporting} style={btnGhost()}>
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      )}

      {tab === "purchases" && loading && <p style={{ color: COLORS.muted }}>Loading purchases…</p>}

      {tab === "purchases" && !loading && (
        <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                {["User", "Plan", "Amount", "Status", "Stripe subscription", "Date", ""].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "10px 12px", color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {purchases.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: 20, textAlign: "center", color: COLORS.muted }}>
                    No purchases match the selected filters.
                  </td>
                </tr>
              ) : (
                purchases.map((p) => (
                  <tr key={p.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: "10px 12px" }}>{p.userName}</td>
                    <td style={{ padding: "10px 12px" }}>{planLabel(p.plan)}</td>
                    <td style={{ padding: "10px 12px" }}>{formatMoney(p.amountCents)}</td>
                    <td style={{ padding: "10px 12px" }}>{p.status}</td>
                    <td style={{ padding: "10px 12px" }}>
                      {p.stripeSubscriptionUrl ? (
                        <a href={p.stripeSubscriptionUrl} target="_blank" rel="noreferrer" style={{ color: COLORS.indigo, fontSize: 12 }}>
                          {p.stripeSubscriptionId}
                        </a>
                      ) : (
                        <span style={{ color: COLORS.muted }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: "10px 12px", color: COLORS.muted }}>{new Date(p.createdAt).toLocaleString()}</td>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                      {p.status === "completed" && p.stripeSubscriptionId && (
                        <button
                          type="button"
                          onClick={() => refundPurchase(p)}
                          disabled={refundingId === p.id}
                          style={{ ...btnGhost(), padding: "6px 10px", color: COLORS.red, borderColor: "rgba(239,68,68,0.3)" }}
                        >
                          {refundingId === p.id ? "Refunding…" : "Refund"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {detailUser && (
        <UserDetailModal
          userId={detailUser.id}
          onClose={() => setDetailUser(null)}
          onEdit={(u) => {
            setDetailUser(null);
            setEditUser(u);
          }}
        />
      )}
      {editUser && (
        <UserEditModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={() => loadUsers(search)}
        />
      )}
    </PanelShell>
  );
}
